import type { Letter } from './types';

export interface NearestMatchOptions {
  columns: number;
  verticalWeight?: number;
  maxDistance?: number;
}

const LOWER_A = 'a'.charCodeAt(0);
const LOWER_Z = 'z'.charCodeAt(0);

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function letterIndex(letter: string): number {
  const code = letter.charCodeAt(0);
  return clamp(code - LOWER_A, 0, LOWER_Z - LOWER_A);
}

function columnFromLetter(letter: string, columns: number): number {
  if (columns <= 1) {
    return 0;
  }
  const ratio = letterIndex(letter) / (LOWER_Z - LOWER_A);
  const column = Math.round(ratio * (columns - 1));
  return clamp(column, 0, columns - 1);
}

function columnToX(column: number, columns: number): number {
  const width = 1 / Math.max(columns, 1);
  return (column + 0.5) * width;
}

function distanceToIdeal(
  letter: Letter,
  idealX: number,
  verticalWeight: number
): number {
  const dx = Math.abs(letter.x - idealX);
  const dy = Math.max(0, letter.y);
  return dx + dy * verticalWeight;
}

export function findNearestMatchingLetter(
  letters: readonly Letter[],
  rawKey: string,
  options: NearestMatchOptions
): Letter | null {
  if (!rawKey) {
    return null;
  }
  const key = rawKey.toLowerCase();
  if (key.length !== 1 || key < 'a' || key > 'z') {
    return null;
  }

  const columns = Math.max(1, options.columns);
  const verticalWeight = options.verticalWeight ?? 0;
  const idealColumn = columnFromLetter(key, columns);
  const idealX = columnToX(idealColumn, columns);

  let bestLetter: Letter | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const letter of letters) {
    if (!letter.alive || letter.ch !== key) {
      continue;
    }
    const distance = distanceToIdeal(letter, idealX, verticalWeight);
    if (options.maxDistance !== undefined && distance > options.maxDistance) {
      continue;
    }
    if (distance < bestDistance) {
      bestDistance = distance;
      bestLetter = letter;
    } else if (distance === bestDistance && bestLetter !== null) {
      if (letter.y > bestLetter.y) {
        bestLetter = letter;
      }
    }
  }

  return bestLetter;
}
